import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationStore } from '../../store/notificationStore';
import { NotificationDto } from '../../api/notifications';

const TYPE_LABELS: Record<string, string> = {
  AccountOpeningRequested: 'New Account Request',
  AccountApproved:         'Account Approved',
  AccountRejected:         'Account Rejected',
  PaymentStatusChanged:    'Payment Update',
  SupportTicketCreated:    'New Support Ticket',
  SupportTicketReopened:   'Ticket Reopened',
};

/** Map notification type → { path, state } */
function getNavigation(n: NotificationDto): { path: string; state?: object } | null {
  switch (n.type) {
    case 'AccountOpeningRequested':
      return { path: '/accounts', state: { tab: 'requests' } };
    case 'PaymentStatusChanged':
      return { path: '/payments' };
    case 'SupportTicketCreated':
    case 'SupportTicketReopened':
      return { path: '/support-tickets' };
    default:
      return null;
  }
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const { notifications, unreadCount, markAllRead, markOneRead } = useNotificationStore();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleItemClick = async (n: NotificationDto) => {
    if (!n.isRead) await markOneRead(n.id);

    const nav = getNavigation(n);
    if (nav) {
      setOpen(false);
      navigate(nav.path, { state: nav.state });
    }
  };

  return (
    <div ref={ref} className="relative">
      {/* Bell button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        aria-label="Notifications"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="font-semibold text-gray-800 text-sm">Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead()}
                className="text-xs text-blue-600 hover:underline"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-400 text-sm">
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => {
                const isClickable = !!getNavigation(n);
                return (
                  <div
                    key={n.id}
                    onClick={() => handleItemClick(n)}
                    className={`px-4 py-3 border-b border-gray-50 transition-colors ${
                      !n.isRead ? 'bg-blue-50' : ''
                    } ${isClickable ? 'cursor-pointer hover:bg-blue-100' : 'cursor-default hover:bg-gray-50'}`}
                  >

                    <div className="flex items-start gap-3">
                      <span className={`mt-1.5 flex-shrink-0 w-2 h-2 rounded-full ${!n.isRead ? 'bg-blue-500' : 'bg-transparent'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                          {TYPE_LABELS[n.type] ?? n.type}
                        </p>
                        <p className="text-sm font-medium text-gray-800 leading-snug">{n.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5 leading-snug">{n.message}</p>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-gray-400">{timeAgo(n.createdAt)}</p>
                          {isClickable && (
                            <span className="text-xs text-blue-500 font-medium">View →</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
